import { useAppSelector } from "store/hooks";
import { useEffect, useMemo, useState } from "react";
import Template, { TemplateType } from "types/Template";
import { VisualLayout } from "types/Workflow";
import ApiService from "services/api.service";
import { ApiConfig } from "../../constants";
import { Segment } from "types/Segment";
import { convertToJounreyVisualLayout as convertToJourneyVisualLayout, toJourneyPayloadTemplate } from "./placeholders";
import { useParams } from "react-router-dom";
import { NodeType } from "./FlowEditor";
import { toast } from "react-toastify";
import { Query, SegmentsSettings } from "reducers/flow-builder.reducer";
import { JourneyStatus } from "pages/JourneyTablev2/JourneyTablev2";
import { useNavigate } from "react-router-dom";
import { delay } from "utils";

interface SimpleJourney {
  channel: string;
  template?: {
    id: string;
    name: string;
  }
  audienceSegment: string;
};

const channelsOptions = [
  { key: TemplateType.EMAIL, title: 'Email' },
  { key: TemplateType.SMS, title: 'SMS' },
  { key: TemplateType.WHATSAPP, title: 'WhatsApp' },
  { key: TemplateType.PUSH, title: 'Push' },
];

export const useSimpleJourneyOptions = () => {
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [templateList, setTemplateList] = useState<Template[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);


  const loadSegments = async () => {
    const { data: { data } } = await ApiService.get<{ data: Segment[] }>({ url: "/segments" });
    setSegments(data);
  };

  const getAllTemplates = async () => {
    const { data: templates } = await ApiService.get<{ data: Template[] }>({
      url: `${ApiConfig.getAllTemplates}`,
    });

    const filteredTemplates = templates?.data?.filter(
      (item: { type?: string }) => item.type === selectedChannel
    );
    setTemplateList(filteredTemplates);
  };

  useEffect(() => {
    if (selectedChannel) {
      getAllTemplates();
    }
  }, [selectedChannel, segments]);

  useEffect(() => {
    loadSegments();
  }, []);

  return {
    channelsOptions,
    templatesOptions: templateList.map((item) => ({
      key: item.id,
      title: item.name,
    })),
    audienceOptions: segments.map((item) => ({
      key: item.id,
      title: item.name,
    })),
    onChannelChange: setSelectedChannel,
  };
}

export const useJourneyControls = () => {
  const [simpleJourney, setSimpleJourney] = useState<SimpleJourney>({ channel: 'email', audienceSegment: '' });
  const [journeyVisualLayout, setJourneyVisualLayout] = useState<VisualLayout>({} as VisualLayout);
  const [stepsIds, setStepsIds] = useState<{ messageStepId: string, exitStepId: string }>({ messageStepId: '', exitStepId: '' });
  const {
    // flowId, 
    nodes,
    edges,
    flowName,
    segments,
    // journeyType,
    // journeyEntrySettings,
    // journeySettings,
    flowStatus
  } = useAppSelector((state) => state.flowBuilder);
  const startStepIdFromJourney = nodes.find((node) => node.type === NodeType.START)?.data?.stepId;
  const { id: journeyId } = useParams();
  const isActive = flowStatus === JourneyStatus.ACTIVE
  const navigate = useNavigate();

  //@ts-ignore
  const journeySegmentId = useMemo(() => segments.query?.statements?.[0]?.segmentId, [segments.query?.statements?.[0]?.segmentId]);
  const messageStepData = useMemo(() => nodes.find((node) => node.type === NodeType.MESSAGE)?.data, [JSON.stringify(nodes)]);

  useEffect(() => {
    const { template = {}, type } = (messageStepData || {}) as any;
    setSimpleJourney({
      audienceSegment: journeySegmentId,
      channel: template?.type,
      template: {
        id: template?.selected?.id,
        name: template?.selected?.name
      }
    })
  }, [messageStepData, journeySegmentId]);

  const updateJourneyLayout = async (visualLayout: VisualLayout) => {
    // if a journey it has a valid data and not in active state
    console.log({
      visualLayout,
      journeyId,
      journeySegmentId,
      flowStatus
    })
    if (
      !visualLayout?.nodes ||
      !visualLayout?.edges ||
      !journeyId ||
      flowStatus === JourneyStatus.ACTIVE
    ) return toast.error("Invalid data");

    if (!startStepIdFromJourney) return;
    await ApiService.patch({
      url: "/journeys/visual-layout",
      options: {
        id: journeyId,
        nodes: visualLayout.nodes,
        edges: visualLayout.edges,
      },
    });
  }

  const onUpdateVisualLayoutMessageTemplate = (template: any) => {
    const visualLayout = convertToJourneyVisualLayout({
      journeyId: journeyId,
      startStepId: startStepIdFromJourney!,
      messageStepId: stepsIds.messageStepId,
      exitStepId: stepsIds.exitStepId,
      template: template
    });
    return visualLayout
  }

  const updateJourney = async ({ id, name, selectedSegmentId }: { id: string, name: string, selectedSegmentId: string }) => {
    if (!id || !name || !selectedSegmentId) return toast.error("Please provide a name and audience segment");
    if (flowStatus !== JourneyStatus.DRAFT) return toast.error("You can't update an active journey");
    const journeyPayload = toJourneyPayloadTemplate({
      id,
      name,
      selectedSegmentId
    });
    const canStartJourney = simpleJourney.channel && simpleJourney.template?.id && simpleJourney.audienceSegment;
    if (!canStartJourney) {
      toast.error("Please select a channel, template and audience segment");
      return;
    }
    const visualLayout = onUpdateVisualLayoutMessageTemplate({
      type: simpleJourney.channel,
      selected: {
        id: simpleJourney.template?.id,
        name: simpleJourney.template?.name
      }
    })
    await updateJourneyLayout(visualLayout as unknown as VisualLayout);
    await ApiService.patch({
      url: "/journeys",
      options: {
        ...journeyPayload,
        visualLayout: visualLayout,
      },
    });
  }

  const onSaveJourneyAsDraft = async () => {
    updateJourney({ id: journeyId, name: flowName, selectedSegmentId: simpleJourney.audienceSegment });
  }

  const startJourney = async () => {
    await ApiService.patch({ url: "/journeys/start/" + journeyId });
    toast.success("Journey started successfully");
    await delay(1000);
    navigate(`/flow/${journeyId}/view`);
  }

  const createSteps = async () => {
    const neededStepsTypes = [
      NodeType.MESSAGE,
      NodeType.EXIT
    ];
    const stepsFetcher = neededStepsTypes.map((type) => ApiService.post({
      url: "/steps",
      options: {
        type,
        journeyID: journeyId
      }
    }));

    const createdStepsIds = (await Promise.all(stepsFetcher)).map((item) => item.data.id);
    const [messageStepId, exitStepId] = createdStepsIds;
    const visualLayout = convertToJourneyVisualLayout({
      journeyId: journeyId,
      startStepId: startStepIdFromJourney!,
      messageStepId: messageStepId,
      exitStepId: exitStepId,
    });
    setStepsIds({ messageStepId: messageStepId, exitStepId: exitStepId });
    setJourneyVisualLayout(visualLayout as unknown as VisualLayout);
  }



  useEffect(() => {
    if (flowStatus && flowStatus !== JourneyStatus.DRAFT) return;
    createSteps();
  }, [flowStatus]);


  console.log('simpleJourney', simpleJourney);
  return {
    onStartJourney: startJourney,
    onSaveJourneyAsDraft,
    updateJourney,
    setSimpleJourney,
    simpleJourney,
    isActive,
  };
}
